import type { Cents } from "../types/money";
import { DEFAULT_BEWERKINGSTARIEVEN } from "./bewerkingstarieven";

/**
 * PP-slijpwerk (platpoly): netto INKOOPprijzen per strekkende meter, per glasfamilie.
 *
 * Bron: de drie werkbladen "PP slijpen uitgelegd" (Thijs, 22-07-2026), afgeleid van de
 * Timmers-prijslijst. Bewust hier in code en niet uit de API-sync: `variant.ops.slijpPerM` stond
 * op 60% van deze bedragen (één vaste prijs voor 3 t/m 6 mm), en zou bij elke `npm run sync:all`
 * weer terugvallen. Dit bestand is de bron van waarheid voor slijpwerk; de sync raakt het niet.
 *
 * Alle bedragen in centen, EXCL. btw, en nog ZONDER onze marge — die komt er runtime overheen via
 * dezelfde categoriefactor als het glas (afspraak: bewerkingen volgen de glasmarge).
 */

/** Glasfamilies met elk hun eigen slijptabel en eigen regels. */
export type GrindFamily = "enkel" | "gelaagd" | "gehard-gelaagd";

/**
 * Enkel glas — sleutel is de glasdikte in mm.
 * Float, mat, gehard, figuur, draad, spiegels en alle gekleurde soorten delen deze tabel.
 *
 * 3 mm staat niet in het werkblad; die volgt de 4 mm-prijs, net zoals de API 3 t/m 6 mm op één
 * tarief zette. Duikt er een dikte op die hier niet staat, dan pakken we de dichtstbijzijnde
 * dikte eronder (zie {@link ppPerMeterCents}) in plaats van op €0 uit te komen.
 */
export const PP_ENKEL: Readonly<Record<number, Cents>> = {
  3: 320,
  4: 320,
  5: 324,
  6: 324,
  8: 528,
  10: 633,
  12: 748,
  15: 1700,
  19: 2292,
};

/** Gelaagd glas — sleutel is de opbouw zoals de klant 'm kiest ("4.4", "8.8", …). */
export const PP_GELAAGD: Readonly<Record<string, Cents>> = {
  "3.3": 607,
  "4.4": 692,
  "5.5": 935,
  "6.6": 1158,
  "8.8": 1770,
  "10.10": 1887,
  "12.12": 2097,
};

/**
 * Gehard-gelaagd — géén oppervlaktetrappen, en het tarief hangt af van de RUITDIKTE, niet van de
 * opbouwcode: de prijslijst (pag. 20) noemt "tot dikte 2x6mm / 2x8mm / 2x10mm / 2x12mm".
 * Zo dekken we ook opbouwen die niet letterlijk in de lijst staan (bv. 88.4 of 1010.2).
 */
export const PP_GEHARD_GELAAGD_BANDEN: readonly { totRuitMm: number; perM: Cents }[] = [
  { totRuitMm: 6, perM: 700 as Cents },
  { totRuitMm: 8, perM: 800 as Cents },
  { totRuitMm: 10, perM: 900 as Cents },
  { totRuitMm: 12, perM: 1500 as Cents },
];

/**
 * Boringen in gehard-gelaagd (prijslijst pag. 20, €/stuk). Ook hier: ruitdikte × gatdiameter.
 * Bij 2x6 mm staat géén prijs voor 6-25 mm in de lijst — die combinatie is dus niet
 * automatisch te prijzen (null → op aanvraag), in plaats van een bedrag te verzinnen.
 */
export const BORING_GEHARD_GELAAGD: readonly {
  totRuitMm: number;
  tot25: Cents | null;
  tot50: Cents;
}[] = [
  { totRuitMm: 6, tot25: null, tot50: 1000 as Cents },
  { totRuitMm: 8, tot25: 800 as Cents, tot50: 1100 as Cents },
  { totRuitMm: 10, tot25: 1100 as Cents, tot50: 1500 as Cents },
  { totRuitMm: 12, tot25: 1700 as Cents, tot50: 2200 as Cents },
];

/**
 * Randuitsparing in gehard-gelaagd (afgescherpt), €/stuk — naar TOTALE pakketdikte.
 * Staat los van de ruitdikte-banden hierboven; zo staat het in de lijst.
 */
export const RANDUITSPARING_GEHARD_GELAAGD: readonly { totMm: number; cents: Cents }[] = [
  { totMm: 10, cents: 4241 as Cents },
  { totMm: 26, cents: 6062 as Cents },
];

/**
 * Extra folie in een gehard-gelaagd pakket (werkblad "Extra folie"): een vaste toeslag PER RUIT
 * voor één extra laag van 0,38 mm — blank of mat. Deze bedragen zijn NETTO: ze gaan zo de
 * prijs in, zonder de inkoopkorting die het glas zelf krijgt (bevestigd door Thijs, 26-07-2026).
 * Alleen de gewone marge loopt er nog overheen, net als bij de andere werkblad-tarieven.
 */
export const EXTRA_FOLIE_CENTS: Readonly<Record<"blank" | "mat", Cents>> = {
  blank: 1800 as Cents,
  mat: 2200 as Cents,
};

/** Dikste ruit uit een opbouwcode ("1212.4" → 12). 0 als het geen code is. */
export function maxPaneMm(code: string | number | undefined): number {
  const panes = panesFromCode(String(code ?? ""));
  return panes ? Math.max(...panes) : 0;
}

/** Boorprijs voor gehard-gelaagd; null = niet in de lijst → op aanvraag. */
export function boringGehardGelaagdCents(code: string | number, diameterMm: number): Cents | null {
  const ruit = maxPaneMm(code);
  const band = BORING_GEHARD_GELAAGD.find((b) => ruit <= b.totRuitMm);
  if (!band) return null;
  return diameterMm <= 25 ? band.tot25 : diameterMm <= 50 ? band.tot50 : null;
}

/**
 * Oppervlaktetrappen op het slijptarief: hoe groter de plaat, hoe duurder per meter.
 * Geldt voor enkel glas én gelaagd; gehard-gelaagd heeft één vast tarief.
 * De grens is exclusief: precies 3 m² valt nog in de basis.
 */
export const PP_AREA_TIERS: readonly { boven: number; factor: number }[] = [
  { boven: 10, factor: 2.5 },
  { boven: 6, factor: 2 },
  { boven: 3, factor: 1.5 },
];

export function ppAreaFactor(areaM2: number, family: GrindFamily): number {
  if (family === "gehard-gelaagd") return 1;
  for (const t of PP_AREA_TIERS) if (areaM2 > t.boven) return t.factor;
  return 1;
}

/**
 * Minimaal te berekenen strekkende meters. Een rechte plaat rekent altijd minstens 1 m1 af;
 * bij enkel glas rekent een model (niet-rechthoek) minstens 2 m1 — gelaagd en gehard-gelaagd
 * blijven ook bij een model op 1 m1.
 */
export function minGrindMeters(family: GrindFamily, isModel: boolean): number {
  if (!isModel) return 1;
  return family === "enkel" ? 2 : 1;
}

/**
 * Vormtoeslag op het SLIJPWERK — stap 5 van de werkbladen ("Geldt er een toeslag door het model?").
 *
 * De tabel noemt 100% bij een schuine zijde, cirkel, gebogen zijde en ovaal (bij gelaagd 50% voor
 * de schuine zijde), met de notitie "Op het glas en het slijpwerk". Die notitie is letterlijk
 * bedoeld: laat je een model PP of facet slijpen, dan wordt niet alleen het slijpwerk maar ook de
 * slijptarief duurder. Op de GLASprijs komt hij niet: de volgorde is glas → vorm → bewerking.
 * (Eerder stond hier dat het ook op het glas drukte; door Thijs teruggedraaid op 28-07-2026 — het
 * slijpwerk ging; door Thijs gecorrigeerd op 26-07-2026 na een prijstest.)
 */
export function ppShapeFactor(family: GrindFamily, shapeId: string | undefined): number {
  if (!shapeId || shapeId === "rechthoek") return 1;
  if (isSchuineZijde(shapeId)) return family === "gelaagd" ? 1.5 : 2;
  if (isRondOfGebogen(shapeId)) return 2;
  return 1;
}


/**
 * Is dit een randafwerking waarvoor de glasplaat de modeltoeslag hierboven krijgt?
 *
 * Alleen écht slijpwerk: platpoly (PP) en facet (met of zonder PP). Afscherpen telt niet mee —
 * daar wordt alleen de scherpe rand gebroken, en het glas houdt zijn gewone modeltoeslag.
 */
/** Is dit een FACET-afwerking (met of zonder doorlopende PP)? */
/**
 * Modeltoeslag op het GLAS omdat dat model geslepen wordt — "Modeltoeslag ivm slijpen" uit het
 * rekenblad "Berekening PP slijpen" (Thijs, 29-07-2026). Zelfde bron als `ppShapeFactor`, dus
 * dezelfde percentages: 100% bij schuine zijde/cirkel/toog/ovaal, 50% bij gelaagd met een
 * schuine zijde, niets bij een rechthoek.
 *
 * Het rekenblad vermenigvuldigt: glasprijs × (1 + deze toeslag) × (1 + vormtoeslag).
 */
export function glasModelPctBijSlijpen(family: GrindFamily, shapeId: string | undefined): number {
  return (ppShapeFactor(family, shapeId) - 1) * 100;
}

export function isFacetAfwerking(finishId: string | undefined): boolean {
  if (!finishId) return false;
  const kaal = finishId.replace(/\+pp$/, "");
  return (Object.keys(FACET_BAND_EDGE_ID) as FacetBand[]).some((b) => FACET_BAND_EDGE_ID[b] === kaal);
}

export function isSlijpAfwerking(finishId: string | undefined): boolean {
  if (!finishId) return false;
  if (finishId === "polijst") return true;
  const kaal = finishId.replace(/\+pp$/, "");
  return (Object.keys(FACET_BAND_EDGE_ID) as FacetBand[]).some((b) => FACET_BAND_EDGE_ID[b] === kaal);
}

/** Vormen met één of meer schuine zijden — ongeacht het aantal geldt dezelfde toeslag. */
function isSchuineZijde(shapeId: string): boolean {
  return /schuine|achthoek/i.test(shapeId);
}

/** Cirkel, ovaal, gebogen zijde en toog. */
function isRondOfGebogen(shapeId: string): boolean {
  return /cirkel|ovaal|gebogen|toog|segment/i.test(shapeId);
}

/**
 * Minimumbedrag voor het SLIJPWERK bij een RONDE vorm: cirkel, ovaal, toog en gebogen zijde
 * kosten minstens € 30 aan slijpwerk (gelaagd € 40). Rekenblad "Berekening PP slijpen":
 * `IF(OR(model="cirkel";"toog";"ovaal"); IF(bedrag<30; 30; bedrag); bedrag)`.
 *
 * Een schuine zijde of achthoek valt er dus NIET onder — die worden rechtlijnig geslepen.
 * Alleen bij écht slijpwerk (PP, facet, facet + PP). Afscherpen en "geen randafwerking" hebben
 * geen minimum. Geen minimum → 0.
 *
 * (Stond eerst voor elk model aan; door Thijs gecorrigeerd op 29-07-2026 — het rekenblad had het
 * goed en de eerdere doorgifte niet.)
 */
export const SLIJP_MODEL_MIN_CENTS = DEFAULT_BEWERKINGSTARIEVEN.slijpModelMinCents;
export const SLIJP_MODEL_MIN_GELAAGD_CENTS = DEFAULT_BEWERKINGSTARIEVEN.slijpModelMinGelaagdCents;

export function slijpModelMinimumCents(family: GrindFamily, shapeId: string | undefined): Cents {
  if (!shapeId || !isRondOfGebogen(shapeId)) return 0 as Cents;
  return (family === "gelaagd" ? SLIJP_MODEL_MIN_GELAAGD_CENTS : SLIJP_MODEL_MIN_CENTS) as Cents;
}

/**
 * Slijptarief per strekkende meter (netto inkoop, centen) voor een dikte/opbouw.
 * `key` is de dikte in mm bij enkel glas, of de opbouw-string bij (gehard-)gelaagd.
 * Onbekende dikte → de dichtstbijzijnde bekende dikte eronder, zodat we nooit op €0 uitkomen.
 */
export function ppPerMeterCents(family: GrindFamily, key: number | string): Cents {
  if (family === "gelaagd") {
    const exact = PP_GELAAGD[String(key)];
    if (exact != null) return exact;
    // Asymmetrische opbouw (bv. 84.4 = één ruit van 8 en één van 4). Het werkblad noemt alleen
    // gelijke ruiten, maar het tarief volgt de TOTALE glasdikte: 8 + 4 = 12 mm, dus hetzelfde als
    // 6.6. Zonder deze stap viel zo'n opbouw terug op de catalogusprijs en week het slijpwerk af.
    // De sleutel is hier het RUITENpaar uit grindKeyFor ("8.4" = 8 mm + 4 mm), niet de code van
    // de site — dus gewoon de delen optellen.
    const somVan = (code: string): number =>
      code.split(".").reduce((t, deel) => t + (Number(deel) || 0), 0);
    const totaal = somVan(String(key));
    if (totaal > 0) {
      for (const [code, prijs] of Object.entries(PP_GELAAGD)) {
        if (somVan(code) === totaal) return prijs;
      }
    }
    return 0 as Cents;
  }
  if (family === "gehard-gelaagd") {
    const ruit = maxPaneMm(key);
    return (PP_GEHARD_GELAAGD_BANDEN.find((b) => ruit <= b.totRuitMm)?.perM ?? 0) as Cents;
  }

  const mm = typeof key === "number" ? key : Number.parseFloat(String(key));
  if (!Number.isFinite(mm)) return 0 as Cents;
  const exact = PP_ENKEL[mm];
  if (exact != null) return exact;
  const diktes = Object.keys(PP_ENKEL)
    .map(Number)
    .sort((a, b) => a - b);
  let gekozen = diktes[0]!;
  for (const d of diktes) if (d <= mm) gekozen = d;
  return PP_ENKEL[gekozen]!;
}

/**
 * Slijpkosten voor één paneel (netto inkoop, centen), vóór marge.
 * Volgt de stappen uit het werkblad: tarief per dikte → oppervlaktetrap → minimale meters →
 * vormtoeslag op het slijpwerk.
 */
export function grindingCostCents(opts: {
  family: GrindFamily;
  /** Dikte in mm (enkel) of opbouw-string ((gehard-)gelaagd). */
  key: number | string;
  /** Te slijpen lengte in meters (omtrek, of alleen de gekozen zijden). */
  lengthM: number;
  /** Netto glasoppervlak in m² — bepaalt de oppervlaktetrap. */
  areaM2: number;
  /** Vorm-id; "rechthoek"/leeg = rechte plaat. */
  shapeId?: string;
}): Cents {
  return grindingCostParts(opts).total;
}

export interface GrindingCostParts {
  /** Gefactureerde meters (na het minimum van 1 m1 / 2 m1 bij een model). */
  meters: number;
  /** Kaal tarief per m1 uit het werkblad, vóór toeslagen. */
  perMeter: Cents;
  /** Kale slijpkosten: meters × tarief. */
  base: Cents;
  /** Afmetingtoeslag op het slijpwerk (>3/6/10 m²), in procenten en centen. */
  sizePct: number;
  sizeAmount: Cents;
  /** Vormtoeslag op het slijpwerk (model), in procenten en centen. */
  shapePct: number;
  shapeAmount: Cents;
  total: Cents;
}

/**
 * Dezelfde som als `grindingCostCents`, maar uitgesplitst — zodat de Controle kan laten zien
 * wélk tarief er gerekend is en welke toeslagen erop zitten. Zonder die uitsplitsing zag je
 * alleen het eindtarief per meter (bv. € 12,96 waar de prijslijst € 3,24 noemt).
 */
export function grindingCostParts(opts: {
  family: GrindFamily;
  key: number | string;
  lengthM: number;
  areaM2: number;
  shapeId?: string;
}): GrindingCostParts {
  const { family, key, lengthM, areaM2, shapeId } = opts;
  const perM = ppPerMeterCents(family, key);
  const leeg: GrindingCostParts = {
    meters: 0, perMeter: perM, base: 0 as Cents, sizePct: 0, sizeAmount: 0 as Cents,
    shapePct: 0, shapeAmount: 0 as Cents, total: 0 as Cents,
  };
  if (perM <= 0 || !(lengthM > 0)) return leeg;

  const isModel = !!shapeId && shapeId !== "rechthoek";
  const meters = Math.max(lengthM, minGrindMeters(family, isModel));
  const areaFactor = ppAreaFactor(areaM2, family);
  const shapeFactor = ppShapeFactor(family, shapeId);
  const base = Math.round(meters * perM) as Cents;
  const naArea = Math.round(meters * perM * areaFactor);
  const total = Math.round(meters * perM * areaFactor * shapeFactor) as Cents;
  return {
    meters,
    perMeter: perM,
    base,
    sizePct: Math.round((areaFactor - 1) * 100),
    sizeAmount: (naArea - base) as Cents,
    shapePct: Math.round((shapeFactor - 1) * 100),
    shapeAmount: (total - naArea) as Cents,
    total,
  };
}

/* ────────────────────────────── Facetslijpen ────────────────────────────── */

/**
 * Facet: netto inkoop per strekkende meter, bepaald door de FACETBREEDTE × de glasdikte.
 * Bron: werkblad "Facetslijpen uitgelegd niet gelaagd" (Thijs, 24-07-2026).
 *
 * Anders dan PP kent facet géén oppervlaktetrappen — het tarief per meter staat vast.
 * Niet elke breedte kan bij elke dikte: 4 mm kan alleen de smalste band, en hoe breder het facet,
 * hoe dikker het glas moet zijn. Ontbreekt een combinatie hier, dan is 'ie niet leverbaar.
 */
export type FacetBand = "10-25" | "10-30" | "31-40" | "41-50";

export const FACET_TARIEVEN: Readonly<Record<FacetBand, Readonly<Record<number, Cents>>>> = {
  "10-25": { 4: 1253 },
  "10-30": { 5: 1253, 6: 1253, 8: 1875, 10: 1875, 12: 1875, 15: 2643, 19: 2643 },
  "31-40": { 6: 1490, 8: 2116, 10: 2116, 12: 2116, 15: 2878, 19: 2878 },
  "41-50": { 8: 2357, 10: 2357, 12: 2357, 15: 3125, 19: 3125 },
};

export const FACET_BANDEN: readonly FacetBand[] = ["10-25", "10-30", "31-40", "41-50"];

/** Welke facetbreedtes kan deze dikte aan? Lege lijst = facet niet mogelijk. */
export function facetBandenVoorDikte(thicknessMm: number): FacetBand[] {
  return FACET_BANDEN.filter((b) => FACET_TARIEVEN[b][thicknessMm] != null);
}

/**
 * Facet op een model (toog, cirkel, ovaal, gebogen) vraagt minimaal 6 mm glas; dunner kan het
 * niet geslepen worden. Bij een rechte plaat geldt die ondergrens niet.
 */
export const FACET_MIN_DIKTE_MODEL_MM = 6;

export function facetMogelijk(thicknessMm: number, shapeId?: string): boolean {
  const isModel = !!shapeId && shapeId !== "rechthoek";
  if (isModel && thicknessMm < FACET_MIN_DIKTE_MODEL_MM) return false;
  // Schuine hoeken staan niet in het prijzenboek → facet daar niet aanbieden.
  if (shapeId && /schuine-hoek/i.test(shapeId)) return false;
  return facetBandenVoorDikte(thicknessMm).length > 0;
}

/**
 * Afgeronde hoek met doorlopend facet — prijs per stuk. Een paneel met ronde hoeken moet altijd
 * rondom gepolijst zijn, dus dit kan alleen in combinatie met PP. Bij 19 mm ontbreekt een prijs
 * in het prijzenboek: dan geen automatische prijs (null → op aanvraag).
 */
export function facetRondeHoekCents(thicknessMm: number): Cents | null {
  if (thicknessMm >= 4 && thicknessMm <= 6) return 3750 as Cents;
  if (thicknessMm >= 8 && thicknessMm <= 12) return 4500 as Cents;
  if (thicknessMm === 15) return 5250 as Cents;
  return null; // 19 mm en al het overige: geen prijs bekend
}

/**
 * Facetkosten voor één paneel (netto inkoop, centen), vóór marge.
 *
 * Dezelfde minima en toeslagen als PP-slijpen: een rechte plaat rekent minimaal 1 m1, een MODEL
 * minimaal 2 m1, en op een model verdubbelt het tarief. Dat minimum stond hier op een vaste 1 m1,
 * waardoor een kleine toog of cirkel met facet te weinig meters afrekende.
 *
 * `null` = deze combinatie van dikte, breedte en vorm is niet leverbaar.
 */
export function facetCostCents(opts: {
  band: FacetBand;
  thicknessMm: number;
  lengthM: number;
  shapeId?: string;
}): Cents | null {
  const { band, thicknessMm, lengthM, shapeId } = opts;
  if (!facetMogelijk(thicknessMm, shapeId)) return null;
  const perM = FACET_TARIEVEN[band]?.[thicknessMm];
  if (perM == null || !(lengthM > 0)) return null;
  const isModel = !!shapeId && shapeId !== "rechthoek";
  const meters = Math.max(lengthM, minGrindMeters("enkel", isModel));
  return Math.round(meters * perM * ppShapeFactor("enkel", shapeId)) as Cents;
}

/* ──────────────── Bewerkingen op gelaagd glas / facetbanden per dikte ──────────────── */

/**
 * Opslag op bewerkingen (boring, uitsparing, hoekafwerking) bij GELAAGD glas: +25%.
 *
 * Bevestigd op twee manieren: de werkbladen schrijven "tabel pag. 13/14 aanhouden + 25% voor
 * gelaagd glas", en de Timmers-data zelf bevat één gelaagde boring (ø71-80 = € 41) tegenover
 * € 32,80 gewoon — exact factor 1,25. De API levert alleen díé ene maat gelaagd; alle andere
 * maten leiden we hiermee af in plaats van ze op de (te lage) gewone prijs te laten staan.
 *
 * Voor gehard-gelaagd geldt dit NIET: daarvoor staat een eigen boortabel in het prijzenboek
 * (pag. 20) die we nog niet hebben.
 */
export const LAMINATED_OP_SURCHARGE = 1.25;

export function laminatedOpCents(baseCents: Cents, family: GrindFamily): Cents {
  return (family === "gelaagd" ? Math.round(baseCents * LAMINATED_OP_SURCHARGE) : baseCents) as Cents;
}

/**
 * Randafwerking-ids voor facet die bij deze glasdikte leverbaar zijn, in de vorm die de
 * catalogus gebruikt ("facet30", "facet30+pp", …).
 *
 * Nodig omdat de catalogus alleen de banden 10-30, 31-40 en 41-50 kent: de band 10-25 — de
 * ENIGE die bij 4 mm kan — ontbrak, waardoor facet op 4 mm op een te brede (en te dure) band
 * werd geprijsd of helemaal niet werd aangeboden.
 */
export const FACET_BAND_EDGE_ID: Readonly<Record<FacetBand, string>> = {
  "10-25": "facet25",
  "10-30": "facet30",
  "31-40": "facet40",
  "41-50": "facet50",
};

/** De breedtes (mm) die elke band dekt — de naam van de band, expliciet gemaakt. */
export const FACET_BAND_RANGE: Readonly<Record<FacetBand, { minMm: number; maxMm: number }>> = {
  "10-25": { minMm: 10, maxMm: 25 },
  "10-30": { minMm: 10, maxMm: 30 },
  "31-40": { minMm: 31, maxMm: 40 },
  "41-50": { minMm: 41, maxMm: 50 },
};

/**
 * Instelbare facetbreedte voor een glasdikte: het bereik van álle banden die voor die dikte een
 * prijs hebben. Een facet begint altijd bij 10 mm — smaller staat nergens in het prijzenboek — en
 * hoe dik het glas, hoe breder het facet mag. 4 mm kan alleen 10–25 mm, 5 mm tot 30, 6 mm tot 40,
 * 8 mm en dikker tot 50. `null` = deze dikte kan helemaal geen facet.
 *
 * Dit stuurt de schuifbalk in de configurator: buiten dit bereik bestaat er geen inkoopprijs, dus
 * daar mag de klant niet kunnen komen (een facet van 1 mm op 5 mm glas was zo wél te kiezen).
 */
export function facetWidthRange(thicknessMm: number): { minMm: number; maxMm: number } | null {
  const banden = facetBandenVoorDikte(thicknessMm);
  if (!banden.length) return null;
  return {
    minMm: Math.min(...banden.map((b) => FACET_BAND_RANGE[b].minMm)),
    maxMm: Math.max(...banden.map((b) => FACET_BAND_RANGE[b].maxMm)),
  };
}

/** De band waar deze breedte in valt én die voor deze dikte een prijs heeft. `null` = geen prijs. */
export function facetBandForWidth(widthMm: number, thicknessMm: number): FacetBand | null {
  for (const b of facetBandenVoorDikte(thicknessMm)) {
    const r = FACET_BAND_RANGE[b];
    if (widthMm >= r.minMm && widthMm <= r.maxMm) return b;
  }
  return null;
}

export function facetEdgeFinishIds(thicknessMm: number, shapeId?: string): string[] {
  if (!facetMogelijk(thicknessMm, shapeId)) return [];
  return facetBandenVoorDikte(thicknessMm).flatMap((b) => {
    const id = FACET_BAND_EDGE_ID[b];
    return [id, `${id}+pp`];
  });
}

/** Glasfamilie afleiden uit de soort-slug ("gelaagd-glas", "gehard-gelaagd-glas", …). */
export function grindFamilyForSlug(slug: string | undefined): GrindFamily {
  // Normaliseren: de bron is soms een slug ("gehard-gelaagd-glas") en soms een weergavenaam
  // ("Gehard gelaagd"). Zonder dit mist de match op de spatie-variant.
  const s = (slug ?? "").toLowerCase().replace(/[^a-z]+/g, "-");
  // Volgorde telt: "gehard-gelaagd" bevat ook "gelaagd".
  if (s.includes("gehard-gelaagd")) return "gehard-gelaagd";
  // Beloopbaar glas is zwaar gelaagd → zelfde regels als gelaagd.
  if (s.includes("gelaagd") || s.includes("beloopbaar")) return "gelaagd";
  return "enkel";
}

/**
 * Slijpkosten voor een gekozen randafwerking, op basis van de werkbladen.
 * `null` = deze afwerking staat niet in de werkbladen (verstek, C-slijp, …) → de aanroeper valt
 * terug op de prijs uit de catalogus.
 *
 * Facet + PP ("facet30+pp") is de som van beide bewerkingen: het paneel wordt gefacet én rondom
 * gepolijst.
 */
/** Afscherpen kost de helft van PP-slijpen — één plek, zodat label en prijs niet uiteen lopen. */
export const AFSCHERPEN_PP_FACTOR = 0.5;

/**
 * De uitgesplitste slijpkosten van een randafwerking, voor de Controle. Alleen voor de
 * PP-gebaseerde afwerkingen (platpoly en afscherpen): daar is de opbouw tarief → afmetingtoeslag
 * → vormtoeslag. Facet kent geen oppervlaktetrap, dus daar heeft uitsplitsen geen zin → null.
 */
export function edgeWorksheetParts(
  finishId: string | undefined,
  opts: { family: GrindFamily; key: number | string; lengthM: number; areaM2: number; shapeId?: string },
): GrindingCostParts | null {
  if (finishId !== "polijst" && finishId !== "afscherpen") return null;
  if (ppPerMeterCents(opts.family, opts.key) <= 0) return null;
  const p = grindingCostParts(opts);
  if (finishId === "polijst") return p;
  const f = AFSCHERPEN_PP_FACTOR;
  const round = (c: number): Cents => Math.round(c * f) as Cents;
  return {
    ...p,
    perMeter: round(p.perMeter),
    base: round(p.base),
    sizeAmount: round(p.sizeAmount),
    shapeAmount: round(p.shapeAmount),
    total: round(p.total),
  };
}

export function edgeCostFromWorksheet(
  finishId: string | undefined,
  opts: { family: GrindFamily; key: number | string; thicknessMm: number; lengthM: number; areaM2: number; shapeId?: string },
): Cents | null {
  if (!finishId) return null;
  const { family, key, thicknessMm, lengthM, areaM2, shapeId } = opts;
  // Onbekend tarief (bv. een gelaagde opbouw die niet in de tabel staat) → null, zodat de
  // aanroeper terugvalt op de catalogusprijs. Zonder deze check zou grindingCostCents 0
  // teruggeven en werd het slijpwerk stilzwijgend GRATIS.
  if (ppPerMeterCents(family, key) <= 0) return null;
  const pp = (): Cents => grindingCostCents({ family, key, lengthM, areaM2, shapeId });

  if (finishId === "polijst") return pp();
  // Afscherpen = de halve PP-prijs. Bewust via dezelfde berekening, zodat het óók de
  // oppervlaktetrap, het minimum-m1 en de vormtoeslag meekrijgt.
  if (finishId === "afscherpen") return Math.round(pp() * AFSCHERPEN_PP_FACTOR) as Cents;

  const band = (Object.keys(FACET_BAND_EDGE_ID) as FacetBand[]).find(
    (b) => FACET_BAND_EDGE_ID[b] === finishId.replace(/\+pp$/, ""),
  );
  if (!band) return null; // verstek, C-slijp, onbewerkt → catalogusprijs

  const facet = facetCostCents({ band, thicknessMm, lengthM, shapeId });
  if (facet == null) return null;
  return (finishId.endsWith("+pp") ? facet + pp() : facet) as Cents;
}

/**
 * De sleutel waarmee je in de slijptabel zoekt.
 *
 * Bij enkel glas is dat de dikte in mm. Bij (gehard-)gelaagd NIET: die varianten dragen intern
 * een offset-`thicknessMm` (bv. 24.02) om opbouwen met dezelfde effectieve mm uit elkaar te
 * houden. De echte sleutel is dan de samenstellingscode ("4.4", "44.2"), die in `thicknessLabel`
 * staat. Zoek je toch op de offset, dan vindt de tabel niets.
 */
export function grindKeyFor(
  family: GrindFamily,
  variant: { thicknessMm?: number; thicknessLabel?: string },
): number | string {
  if (family === "enkel") return variant.thicknessMm ?? 0;
  const code = (variant.thicknessLabel ?? "").trim();
  if (!/^\d+\.\d+$/.test(code)) return variant.thicknessMm ?? 0;
  // Gehard-gelaagd: het werkblad gebruikt exact dezelfde code als de site ("44.2", "1212.4").
  if (family === "gehard-gelaagd") return code;
  // Gelaagd: de site codeert óók het aantal folies ("44.1" en "44.2" zijn allebei 2× 4 mm),
  // het werkblad alleen de ruiten ("4.4"). Slijpen hangt van de ruiten af, niet van de folies.
  const panes = panesFromCode(code);
  return panes ? panes.join(".") : code;
}

/** Ruiten uit een samenstellingscode ("1010.4" → [10, 10]). Null als het geen code is. */
function panesFromCode(code: string): number[] | null {
  const m = code.match(/^(\d+)\.(\d+)$/);
  if (!m) return null;
  const left = m[1]!;
  const panes: number[] = [];
  for (let i = 0; i < left.length; ) {
    // "10" en "12" zijn tweecijferige ruiten; al het overige is één cijfer.
    if (left[i] === "1" && (left[i + 1] === "0" || left[i + 1] === "2")) {
      panes.push(Number(left.slice(i, i + 2)));
      i += 2;
    } else {
      panes.push(Number(left[i]));
      i++;
    }
  }
  return panes.every((p) => Number.isFinite(p) && p > 0) && panes.length ? panes : null;
}

/**
 * Productiegrens gehard-gelaagd (prijslijst pag. 20): een 44.2 boven 2,5 m² maakt Timmers niet —
 * die moet naar minimaal 55.2. Geeft de vereiste opbouw terug, of null als het zo mag.
 *
 * Zonder deze check kan een klant online een 44.2 van 3 m² bestellen die bij de leverancier
 * stukloopt; dat merk je dan pas ná de betaling.
 */
export const GEHARD_GELAAGD_44_2_MAX_M2 = 2.5;

export function gehardGelaagdMinOpbouw(code: string | undefined, areaM2: number): string | null {
  if ((code ?? "").trim() !== "44.2") return null;
  return areaM2 > GEHARD_GELAAGD_44_2_MAX_M2 ? "55.2" : null;
}

/**
 * Toeslagen gehard-gelaagd (prijslijst pag. 20). Dit zijn PANEELtoeslagen — een percentage over
 * het glas — en NIET een prijs per stuk. Bij enkel glas kost een ronde hoek een vast bedrag per
 * hoek; hier kost hij 120% van het paneel, ongeacht het aantal hoeken. Die twee door elkaar
 * halen levert onzinprijzen op, vandaar deze aparte tabel.
 */
export const GEHARD_GELAAGD_TOESLAG_PCT = {
  schuineZijde: 40,
  rondeHoek: 120,
  cirkel: 120,
  ovaal: 120,
} as const;

/**
 * Paneeltoeslag-% voor gehard-gelaagd op basis van vorm en hoekafwerking.
 * Vorm en ronde hoek stapelen NIET: de prijslijst noemt één toeslag per paneel, dus we nemen de
 * hoogste die van toepassing is.
 */
export function gehardGelaagdToeslagPct(opts: { shapeId?: string; heeftRondeHoek?: boolean }): number {
  const pcts: number[] = [];
  const id = opts.shapeId ?? "";
  if (/cirkel/i.test(id)) pcts.push(GEHARD_GELAAGD_TOESLAG_PCT.cirkel);
  if (/ovaal/i.test(id)) pcts.push(GEHARD_GELAAGD_TOESLAG_PCT.ovaal);
  if (/schuine|achthoek/i.test(id)) pcts.push(GEHARD_GELAAGD_TOESLAG_PCT.schuineZijde);
  if (opts.heeftRondeHoek) pcts.push(GEHARD_GELAAGD_TOESLAG_PCT.rondeHoek);
  return pcts.length ? Math.max(...pcts) : 0;
}

/**
 * Grootte-toeslag op het GLAS (prijslijst pag. 5): binnen 2400 × 4500 mm geen toeslag, daarbuiten
 * +50%. Let op: dit is een MAAT-regel, geen oppervlakte-regel.
 *
 * NOG NIET IN GEBRUIK — bewust. Het lag voor de hand dat deze maat-regel de oppervlakte-staffel
 * (+50% >3 m²) op het glas zou vervangen, want pag. 13 zegt dat die staffel "enkel van toepassing
 * is op de randbewerking". Maar een live afgerekende Timmers-order (2000×1800 = 3,62 m²) toont
 * wél een "Grootte toeslag 50%" over het glas, terwijl die plaat ruim binnen 2400 × 4500 mm valt
 * (zie timmers-parity.test.ts). De twee regels bestaan dus naast elkaar, en waarschijnlijk komt
 * deze +50% er bovenop bij écht grote platen. Zet 'm pas aan na een live-controle bij Timmers met
 * een plaat buiten 2400 × 4500 mm — anders reken je mogelijk dubbel.
 */
export const GLAS_TOESLAG_MAX_KORT_MM = 2400;
export const GLAS_TOESLAG_MAX_LANG_MM = 4500;
export const GLAS_TOESLAG_PCT = 50;

export function glassSizeSurchargePct(widthMm: number, heightMm: number): number {
  if (!(widthMm > 0) || !(heightMm > 0)) return 0;
  const kort = Math.min(widthMm, heightMm);
  const lang = Math.max(widthMm, heightMm);
  return kort > GLAS_TOESLAG_MAX_KORT_MM || lang > GLAS_TOESLAG_MAX_LANG_MM ? GLAS_TOESLAG_PCT : 0;
}
